import { PaginatedResponseV2 } from 'clipsal-cortex-types/src/common/types';
import { getStringifiedQueryParams } from 'clipsal-cortex-utils/src/formatting/query-params';

import { InvitationStatus, InvitedUserData, UserData } from '../../../../api/api-user';
import { baseApi } from '../../../../app/baseApi';
import { SearchUserQueryParams } from './manage-users-helpers';

type UserUpdateFields = Pick<UserData, 'user_email' | 'user_first_name' | 'user_last_name' | 'user_phone' | 'role'>;
type UserInviteFields = Pick<UserData, 'user_email' | 'role' | 'tenant_id'>;

export const manageUsersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUser: builder.query<UserData, { id: string; status: InvitationStatus }>({
      query: ({ id, status }) => ({
        url: status === 'CONFIRMED' ? `/fleet/v2/users/${id}` : `/fleet/invitations/${id}`,
        method: 'GET',
      }),
      providesTags: (_, __, { id, status }) => [{ type: 'User', id, status }],
    }),
    getUsers: builder.query<PaginatedResponseV2<UserData>, { params: SearchUserQueryParams; status: InvitationStatus }>(
      {
        query: ({ status, params }) => {
          // not sure why string needs to casted to generic type to make types work
          const stringifiedParams = getStringifiedQueryParams<string>(params);
          const url = `${status === 'CONFIRMED' ? '/fleet/v2/users' : '/fleet/invitations'}?${stringifiedParams}`;

          return {
            url,
            method: 'GET',
          };
        },
        providesTags: (_, __, { status, params }) => [{ type: 'Users', status, ...params }],
      }
    ),
    inviteNewUser: builder.mutation<InvitedUserData, UserInviteFields>({
      query: (body) => {
        return {
          url: `/fleet/invitations`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: () => [{ type: 'Users', status: 'INVITED' }],
    }),
    updateInvitedUser: builder.mutation<void, { body: Partial<UserUpdateFields>; username: string }>({
      query: ({ body, username }) => {
        return {
          url: `/fleet/invitations/${username}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: (_, __, { username }) => [
        { type: 'Users', status: 'INVITED' },
        { type: 'User', id: username, status: 'CONFIRMED' },
        { type: 'InvitedSiteUsers' },
      ],
    }),
    resendInvitation: builder.mutation<void, string>({
      query: (username) => {
        return {
          url: `/fleet/invitations/${username}/resend`,
          method: 'POST',
          body: {},
        };
      },
    }),
    updateConfirmedUser: builder.mutation<void, { body: Partial<UserUpdateFields>; userId: number }>({
      query: ({ body, userId }) => {
        return {
          url: `/fleet/v2/users/${userId}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: (_, __, { userId }) => [
        { type: 'Users', status: 'CONFIRMED' },
        { type: 'User', id: userId, status: 'CONFIRMED' },
        { type: 'SiteUsers' },
      ],
    }),
    deleteConfirmedUser: builder.mutation<void, number>({
      query: (userId) => {
        return {
          url: `/fleet/v2/users/${userId}`,
          method: 'DELETE',
          body: {},
        };
      },
      invalidatesTags: () => [{ type: 'Users', status: 'CONFIRMED' }],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetUsersQuery,
  useGetUserQuery,
  useInviteNewUserMutation,
  useResendInvitationMutation,
  useDeleteConfirmedUserMutation,
  useUpdateInvitedUserMutation,
  useUpdateConfirmedUserMutation,
} = manageUsersApi;

export const useGetUser = ({
  id,
  status,
  skip = false,
}: {
  id: string | null;
  status: InvitationStatus;
  skip: boolean;
}) => {
  const { data, isLoading, isFetching } = useGetUserQuery(
    {
      id: (id || '').toString(),
      status,
    },
    { skip: skip || !id }
  );

  return { user: data || null, isLoading, isFetching };
};

export const useGetUsers = (params: SearchUserQueryParams, status: InvitationStatus, pageSize: number) => {
  const { data, isLoading, isFetching } = useGetUsersQuery({ params, status });
  const dataCount = data?.count || 0;
  const totalPages = Math.ceil(dataCount / pageSize);

  return { users: data?.results || [], isLoading, isFetching, dataCount, totalPages };
};
